import { SlideChannelNormalizationResultSummary } from 'api/slideMultiplexChannelNormalizations';
import { DropdownOption } from 'components/atoms/Dropdown/Dropdown';
import { hasCompleteChannelNormalizationResults } from 'components/StudyDashboard/QCMarkers/normalizationInsights';
import { SlideInferenceResults } from 'interfaces/calculateFeatures';
import { CellRulePanelWithRules } from 'interfaces/cellRule';
import { CellRules } from 'interfaces/jobs/multiplex/cellTypingParams';
import { CaseSlideThumbnailData } from 'interfaces/procedure';
import { QCImpactId } from 'interfaces/qcMarkers';
import { MULTIPLEX_STAIN_ID } from 'interfaces/stainType';
import {
  compact,
  filter,
  forEach,
  groupBy,
  head,
  includes,
  isEmpty,
  join,
  map,
  reduce,
  size,
  some,
  uniq,
  values,
} from 'lodash';
import {
  isAllChannelsOption,
  isAllIncludingExcludedChannelsOption,
  isRemoveAllChannelsOption,
} from 'utils/queryHooks/useChannelsOptions';
import { CasesParams } from 'utils/useCasesParams/types';

export function convertToCellRules(panel: CellRulePanelWithRules): CellRules | null {
  const cellRules: CellRules = {};

  forEach(panel?.rules, (rule) => {
    const { cellTypeId, stainTypeId, ruleValue } = rule;
    // we don't need the whole tree of cell types, just the last part
    const cellType = cellTypeId.split('.').pop();

    if (!cellRules[cellType]) {
      cellRules[cellType] = {};
    }

    cellRules[cellType][stainTypeId] = ruleValue;
  });

  if (isEmpty(cellRules)) {
    // this is for the validation schema
    return null;
  }

  return cellRules;
}

export const getSelectedChannels = (
  newValue: DropdownOption[],
  channelMarkersOptions: DropdownOption[],
  qcMarkersStudySettings?: {
    [markerId: string]: string;
  }
): string[] => {
  if (some(newValue, (option) => isAllChannelsOption(option))) {
    const selectedChannels = map(channelMarkersOptions, 'value');
    if (qcMarkersStudySettings) {
      return filter(selectedChannels, (channelId) => qcMarkersStudySettings?.[channelId] !== QCImpactId.EXCLUDE);
    }
    return selectedChannels;
  } else if (some(newValue, (option) => isAllIncludingExcludedChannelsOption(option))) {
    return map(channelMarkersOptions, 'value');
  } else if (some(newValue, (option) => isRemoveAllChannelsOption(option))) {
    return [];
  } else {
    return map(newValue, 'value');
  }
};

export const synchronizeSelectedChannelProfiles = <ChannelProfile>({
  selectedChannelNames,
  profiles,
  buildDefaultProfile,
}: {
  selectedChannelNames: string[];
  profiles?: Record<string, ChannelProfile> | null;
  buildDefaultProfile: () => ChannelProfile;
}) => {
  return reduce(
    selectedChannelNames,
    (accumulator, channelName) => {
      accumulator[channelName] = profiles?.[channelName] ?? buildDefaultProfile();
      return accumulator;
    },
    {} as Record<string, ChannelProfile>
  );
};

export const resolveNormalizationSelectedSlides = ({
  casesParams,
  slideThumbnailsData,
}: {
  casesParams: CasesParams;
  slideThumbnailsData?: CaseSlideThumbnailData[];
}) => {
  const includedSlideIds = uniq(casesParams.slideIdsToInclude || []);
  const excludedSlideIds = uniq(casesParams.slideIdsToExclude || []);
  const selectedSlideThumbnails = filter(slideThumbnailsData || [], ({ slideId }) => {
    if (size(includedSlideIds) && !includes(includedSlideIds, slideId)) {
      return false;
    }

    if (size(excludedSlideIds) && includes(excludedSlideIds, slideId)) {
      return false;
    }

    return true;
  });
  const selectedSlideIds = size(includedSlideIds)
    ? filter(includedSlideIds, (slideId) => !includes(excludedSlideIds, slideId))
    : uniq(map(selectedSlideThumbnails, 'slideId'));

  return {
    selectedSlideIds,
    selectedSlideThumbnails,
  };
};

export interface MultiplexSelectionResolution {
  effectiveCasesParams: CasesParams | null;
  selectedMultiplexSlideIds: string[];
  selectedMultiplexSlides: SlideInferenceResults[];
  isCaseSelection: boolean;
  infoMessage?: string;
  blockingMessage?: string;
  hasAmbiguousCases?: boolean;
  hasResolvedMultiplexSlides?: boolean;
}

const getCaseSelectionDisplayLabel = (slidesInCase: SlideInferenceResults[] = [], caseId?: string) => {
  const caseLabel = head(compact(map(slidesInCase, 'caseLabel')));

  return caseLabel || `Case ${caseId}`;
};

export const resolveMultiplexActionSelection = ({
  casesParams,
  slides,
  multiplexStainingType = MULTIPLEX_STAIN_ID,
}: {
  casesParams: CasesParams;
  slides?: SlideInferenceResults[];
  multiplexStainingType?: string;
}): MultiplexSelectionResolution => {
  const explicitSlideIds = uniq(casesParams.slideIdsToInclude || []);

  if (size(explicitSlideIds)) {
    return {
      effectiveCasesParams: {
        ...casesParams,
        slidesMode: true,
        caseIdsToInclude: undefined,
        caseIdsToExclude: undefined,
        slideIdsToInclude: explicitSlideIds,
        slideIdsToExclude: undefined,
      },
      selectedMultiplexSlideIds: explicitSlideIds,
      selectedMultiplexSlides: filter(slides || [], ({ slideId }) => includes(explicitSlideIds, slideId)),
      isCaseSelection: false,
      hasResolvedMultiplexSlides: true,
    };
  }

  if (!slides) {
    return {
      effectiveCasesParams: null,
      selectedMultiplexSlideIds: [],
      selectedMultiplexSlides: [],
      isCaseSelection: true,
      hasAmbiguousCases: false,
      hasResolvedMultiplexSlides: false,
    };
  }

  const slidesByCaseId = groupBy(slides, 'caseId');
  const selectedCaseIds = size(casesParams.caseIdsToInclude)
    ? uniq(map(casesParams.caseIdsToInclude, (caseId) => String(caseId)))
    : uniq(map(slides, 'caseId'));

  const ambiguousCases: string[] = [];
  const casesWithoutMultiplexSlides: string[] = [];
  const selectedMultiplexSlides: SlideInferenceResults[] = [];

  forEach(selectedCaseIds, (caseId) => {
    const slidesInCase = slidesByCaseId[caseId] || [];
    const multiplexSlidesInCase = filter(slidesInCase, { stainingType: multiplexStainingType });
    const caseLabel = getCaseSelectionDisplayLabel(slidesInCase, caseId);

    if (size(multiplexSlidesInCase) === 1) {
      selectedMultiplexSlides.push(multiplexSlidesInCase[0]);
      return;
    }

    if (isEmpty(multiplexSlidesInCase)) {
      casesWithoutMultiplexSlides.push(caseLabel);
      return;
    }

    ambiguousCases.push(`${caseLabel} (${size(multiplexSlidesInCase)} multiplex slides)`);
  });

  if (size(ambiguousCases) || size(casesWithoutMultiplexSlides)) {
    return {
      effectiveCasesParams: null,
      selectedMultiplexSlideIds: [],
      selectedMultiplexSlides: [],
      isCaseSelection: true,
      hasAmbiguousCases: size(ambiguousCases) > 0,
      hasResolvedMultiplexSlides: size(selectedMultiplexSlides) > 0,
      blockingMessage: join(
        compact([
          'Select slides before running this multiplex action. Case selection is only allowed when each selected case resolves to exactly one multiplex slide.',
          size(ambiguousCases) ? `Ambiguous cases: ${join(ambiguousCases, ', ')}.` : null,
          size(casesWithoutMultiplexSlides)
            ? `Cases without a multiplex slide in the current selection: ${join(casesWithoutMultiplexSlides, ', ')}.`
            : null,
        ]),
        ' '
      ),
    };
  }

  const selectedMultiplexSlideIds = uniq(map(selectedMultiplexSlides, 'slideId'));

  return {
    effectiveCasesParams: {
      ...casesParams,
      slidesMode: true,
      caseIdsToInclude: undefined,
      caseIdsToExclude: undefined,
      slideIdsToInclude: selectedMultiplexSlideIds,
      slideIdsToExclude: undefined,
    },
    selectedMultiplexSlideIds,
    selectedMultiplexSlides,
    isCaseSelection: true,
    hasAmbiguousCases: false,
    hasResolvedMultiplexSlides: size(selectedMultiplexSlides) > 0,
  };
};

const formatMissingNormalizationCount = (missingSlidesCount: number, totalSlidesCount: number) => {
  if (missingSlidesCount >= totalSlidesCount) {
    return 'all selected slides';
  }

  return `${missingSlidesCount}/${totalSlidesCount} selected slides`;
};

export interface NormalizationCoverageStatus {
  channelName: string;
  channelLabel: string;
  totalSelectedSlidesCount: number;
  normalizedSlideIds: string[];
  missingSlideIds: string[];
  isComplete: boolean;
}

const hasNormalizationResultsForChannel = ({
  channelName,
  slideNormalizationSummaries,
}: {
  channelName: string;
  slideNormalizationSummaries?: SlideChannelNormalizationResultSummary[];
}) => {
  return some(slideNormalizationSummaries || [], (slideNormalizationSummary) => {
    return some(values(slideNormalizationSummary.normalizationsSummary || {}), (channelNormalizationSummary) => {
      return (
        channelNormalizationSummary?.markerType === channelName &&
        hasCompleteChannelNormalizationResults(channelNormalizationSummary?.channelNormalizationResults)
      );
    });
  });
};

const getNormalizationCoverageSlideIds = ({
  normalizationSummaries,
  selectedSlideIds,
}: {
  normalizationSummaries?: SlideChannelNormalizationResultSummary[];
  selectedSlideIds?: string[];
}) => {
  const fallbackSlideIds = uniq(map(normalizationSummaries || [], 'slideId'));
  return uniq(size(selectedSlideIds) ? selectedSlideIds : fallbackSlideIds);
};

export const buildNormalizationCoverageStatuses = ({
  normalizationSummaries,
  selectedChannelNames,
  channelLabelsByName,
  selectedSlideIds,
}: {
  normalizationSummaries?: SlideChannelNormalizationResultSummary[];
  selectedChannelNames: string[];
  channelLabelsByName?: Record<string, string>;
  selectedSlideIds?: string[];
}) => {
  const coverageSlideIds = getNormalizationCoverageSlideIds({
    normalizationSummaries,
    selectedSlideIds,
  });
  const normalizationSummariesBySlideId = groupBy(normalizationSummaries || [], 'slideId');

  return map(selectedChannelNames, (channelName) => {
    const normalizedSlideIds = filter(coverageSlideIds, (slideId) => {
      return hasNormalizationResultsForChannel({
        channelName,
        slideNormalizationSummaries: normalizationSummariesBySlideId[slideId],
      });
    });
    const missingSlideIds = filter(coverageSlideIds, (slideId) => !includes(normalizedSlideIds, slideId));

    return {
      channelName,
      channelLabel: channelLabelsByName?.[channelName] || channelName,
      totalSelectedSlidesCount: size(coverageSlideIds),
      normalizedSlideIds,
      missingSlideIds,
      isComplete: size(missingSlideIds) === 0,
    } as NormalizationCoverageStatus;
  });
};

export const buildNormalizationCoverageWarning = ({
  normalizationSummaries,
  selectedChannelNames,
  channelLabelsByName,
  selectedSlideIds,
  normalizationName,
  actionLabel,
  selectedChannelsLabel,
}: {
  normalizationSummaries?: SlideChannelNormalizationResultSummary[];
  selectedChannelNames: string[];
  channelLabelsByName?: Record<string, string>;
  selectedSlideIds?: string[];
  normalizationName?: string | null;
  actionLabel: string;
  selectedChannelsLabel: string;
}) => {
  if (!size(selectedChannelNames) || normalizationSummaries === undefined) {
    return '';
  }

  const coverageStatuses = buildNormalizationCoverageStatuses({
    normalizationSummaries,
    selectedChannelNames,
    channelLabelsByName,
    selectedSlideIds,
  });
  const totalSlidesCount = coverageStatuses[0]?.totalSelectedSlidesCount || 0;

  if (!totalSlidesCount) {
    return normalizationName
      ? `Normalization coverage could not be verified for the selected slides. ${actionLabel} will still request the normalization result named "${normalizationName}" at execution time.`
      : `Normalization coverage could not be verified for the selected slides. ${actionLabel} may run without validated normalization results.`;
  }

  const missingChannelWarnings = compact(
    map(
      filter(coverageStatuses, (coverageStatus) => !coverageStatus.isComplete),
      (coverageStatus) => {
        return `${coverageStatus.channelLabel} (${formatMissingNormalizationCount(
          size(coverageStatus.missingSlideIds),
          coverageStatus.totalSelectedSlidesCount
        )})`;
      }
    )
  );

  if (!size(missingChannelWarnings)) {
    return '';
  }

  const warningPrefix = normalizationName
    ? `Normalization coverage is missing for ${join(
        missingChannelWarnings,
        ', '
      )}. ${actionLabel} will still request the normalization result named "${normalizationName}" at execution time.`
    : `Selected ${selectedChannelsLabel} are missing normalization coverage for ${join(missingChannelWarnings, ', ')}.`;

  return `${warningPrefix} Results may be inconsistent without normalization.`;
};
